/**
 * persistence/guard/stringGuard.js
 *
 * Stops one match's event string from being saved over another match's.
 *
 *   - Paste check     — confirmStringPaste() flags a pasted string whose events,
 *                       size or jerseys don't look like the current match.
 *   - Copy record     — Version History "Copy" stores a fingerprint + matchId;
 *                       that exact string is refused in any other match.
 *   - Bulk guard      — guardStringUpdate() (called by persistenceMiddleware)
 *                       asks for confirmation before a change that drops a
 *                       large share of the current events, whatever its path.
 *   - Change sources  — what kind of change (edit, paste, restore, …) went into
 *                       each commit, so Firestore history shows how it happened.
 *
 * Flows that already confirmed a whole-string replace (paste, clear, restore,
 * merge) call declareStringIntent() first so the bulk guard doesn't ask again.
 */

import { parseEventTokens } from "../managers/DiffManager";
import {
  STRING_GUARD_MIN_EVENTS,
  STRING_GUARD_MISSING_SHARE,
  STRING_GUARD_COUNT_CHANGE_SHARE,
  STRING_GUARD_UNKNOWN_JERSEY_SHARE,
  COPIED_STRING_KEY,
} from "../config";

// Event fields: team-jersey-action-level-start-end-time-…
const JERSEY_FIELD = 1;
const TIME_FIELD = 6;

// ─── Fingerprint / event keys ─────────────────────────────────────────────────

function _fingerprint(string) {
  const normalized = parseEventTokens(string).join(",");
  let hash = 5381;
  for (let i = 0; i < normalized.length; i++) {
    hash = ((hash << 5) + hash + normalized.charCodeAt(i)) | 0;
  }
  return `${normalized.length}:${(hash >>> 0).toString(16)}`;
}

// Video time + jersey survives label/position edits but differs between matches.
function _eventKey(token) {
  const fields = token.split("-");
  const time = fields[TIME_FIELD];
  if (!time || time === "X") return null;
  return `${time}|${fields[JERSEY_FIELD] ?? ""}`;
}

const _pct = (share) => `${Math.round(share * 100)}%`;

// ─── Assessment ───────────────────────────────────────────────────────────────

/**
 * Compares an incoming string against the current one.
 *
 * @param {string} prevString
 * @param {string} nextString
 * @param {{squadJerseys?: Array<string|number>}} [opts]
 * @returns {{prevCount: number, nextCount: number, missingShare: number,
 *            comparable: boolean, reasons: string[]}}
 */
export function assessReplacement(prevString, nextString, { squadJerseys } = {}) {
  const prev = parseEventTokens(prevString);
  const next = parseEventTokens(nextString);
  const reasons = [];
  const comparable = prev.length >= STRING_GUARD_MIN_EVENTS;

  let missingShare = 0;
  if (comparable) {
    const nextKeys = new Set(next.map(_eventKey).filter(Boolean));
    const prevKeys = prev.map(_eventKey).filter(Boolean);
    if (prevKeys.length > 0) {
      const missing = prevKeys.filter((k) => !nextKeys.has(k)).length;
      missingShare = missing / prevKeys.length;
    }
    if (missingShare >= STRING_GUARD_MISSING_SHARE) {
      reasons.push(
        `${_pct(missingShare)} of the current events (by video time + player) are not in the new string.`
      );
    }
    const countChange = Math.abs(next.length - prev.length) / prev.length;
    if (countChange >= STRING_GUARD_COUNT_CHANGE_SHARE) {
      reasons.push(`Event count changes from ${prev.length} to ${next.length}.`);
    }
  }

  const squad = new Set((squadJerseys ?? []).map((j) => String(j)));
  if (squad.size > 0 && next.length > 0) {
    const jerseys = next
      .map((t) => t.split("-")[JERSEY_FIELD])
      .filter((j) => j && j !== "X");
    if (jerseys.length > 0) {
      const unknown = jerseys.filter((j) => !squad.has(j)).length / jerseys.length;
      if (unknown >= STRING_GUARD_UNKNOWN_JERSEY_SHARE) {
        reasons.push(`${_pct(unknown)} of the new events use jersey numbers not in this match's squads.`);
      }
    }
  }

  return { prevCount: prev.length, nextCount: next.length, missingShare, comparable, reasons };
}

// ─── Copy record ──────────────────────────────────────────────────────────────

/** Called by Version History "Copy" so the string can't be pasted into another match. */
export function recordCopiedString(matchId, string) {
  try {
    localStorage.setItem(
      COPIED_STRING_KEY,
      JSON.stringify({ matchId: String(matchId), fingerprint: _fingerprint(string), copiedAt: Date.now() })
    );
  } catch (err) {
    console.warn("[stringGuard] could not record copied string:", err.message);
  }
}

/** @returns {string|null} the source matchId when `string` was copied from a different match */
export function copiedFromOtherMatch(matchId, string) {
  try {
    const raw = localStorage.getItem(COPIED_STRING_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (!record?.matchId || record.matchId === String(matchId)) return null;
    return record.fingerprint === _fingerprint(string) ? record.matchId : null;
  } catch (err) {
    console.warn("[stringGuard] could not read copied string record:", err.message);
    return null;
  }
}

// ─── Declared intents ─────────────────────────────────────────────────────────

/** matchId → { string, source } for a replace the user has already confirmed. */
const intents = new Map();

export function declareStringIntent(matchId, string, source) {
  intents.set(String(matchId), { string, source });
}

/** @returns {string|null} the declared source when `string` matches the pending intent */
export function consumeStringIntent(matchId, string) {
  const key = String(matchId);
  const intent = intents.get(key);
  if (!intent || intent.string !== string) return null;
  intents.delete(key);
  return intent.source;
}

// ─── Paste check ──────────────────────────────────────────────────────────────

/**
 * Replaces the plain "replace the current string?" confirm on Paste.
 * Refuses strings copied from another match; asks a stronger question when
 * the pasted string doesn't look like this match.
 *
 * @returns {boolean} true when the paste may proceed
 */
export function confirmStringPaste({ matchId, currentString, pastedString, squadJerseys }) {
  const otherMatchId = copiedFromOtherMatch(matchId, pastedString);
  if (otherMatchId) {
    window.alert(
      `This string was copied from match ${otherMatchId}'s Version History.\n` +
        `It cannot be pasted into match ${matchId}.`
    );
    return false;
  }

  const { reasons, prevCount, nextCount } = assessReplacement(currentString, pastedString, { squadJerseys });
  const ok = reasons.length === 0
    ? window.confirm("This will replace the current analysed string. Confirm?")
    : window.confirm(
        `WARNING: the pasted string may belong to a different match (${matchId} is open).\n\n` +
          reasons.map((r) => `• ${r}`).join("\n") +
          `\n\nCurrent: ${prevCount} events · Pasted: ${nextCount} events.\n` +
          "Replace the current string anyway?"
      );
  if (ok) declareStringIntent(matchId, pastedString, reasons.length ? "paste-warned" : "paste");
  return ok;
}

// ─── Bulk guard (middleware) ──────────────────────────────────────────────────

/**
 * Decides whether a SET_FULL_ANALYSE_STRING may replace `prevString`.
 *
 * @returns {{allow: boolean, source: string|null}}
 *   source is the change source to record when the guard was involved.
 */
export function guardStringUpdate(matchId, prevString, nextString) {
  const declared = consumeStringIntent(matchId, nextString);
  if (declared) return { allow: true, source: declared };

  if (prevString === nextString) return { allow: true, source: null };

  const otherMatchId = copiedFromOtherMatch(matchId, nextString);
  if (otherMatchId) {
    window.alert(
      `This string was copied from match ${otherMatchId}'s Version History ` +
        `and cannot be saved to match ${matchId}. The change was not applied.`
    );
    return { allow: false, source: null };
  }

  const { comparable, missingShare, prevCount, nextCount } = assessReplacement(prevString, nextString);
  if (!comparable || missingShare < STRING_GUARD_MISSING_SHARE) return { allow: true, source: null };

  const ok = window.confirm(
    `This change removes or replaces ${_pct(missingShare)} of the events in match ${matchId} ` +
      `(${prevCount} → ${nextCount} events).\n\n` +
      "If you did not mean to replace the whole string, press Cancel.\nApply the change?"
  );
  return { allow: ok, source: ok ? "bulk-replace-confirmed" : null };
}

// ─── Change sources per commit ────────────────────────────────────────────────

/** `${matchId}__${analystId}` → Set of sources since the last commit. */
const pendingSources = new Map();

const _sourceKey = (matchId, analystId) => `${String(matchId)}__${String(analystId)}`;

export function noteChangeSource(matchId, analystId, source) {
  const key = _sourceKey(matchId, analystId);
  if (!pendingSources.has(key)) pendingSources.set(key, new Set());
  pendingSources.get(key).add(source);
}

/** Returns and clears the sources recorded since the previous commit. */
export function takeChangeSources(matchId, analystId) {
  const key = _sourceKey(matchId, analystId);
  const sources = pendingSources.get(key);
  pendingSources.delete(key);
  return sources ? Array.from(sources) : [];
}

// ─── Local edits ──────────────────────────────────────────────────────────────

/**
 * Sessions where this page actually changed the string (not just loaded it).
 * The exit checkpoint uses this so an idle device with a stale string never
 * commits — and pushes — that stale string over newer work from another device.
 */
const editedSessions = new Set();

export function markLocalEdit(matchId, analystId) {
  editedSessions.add(_sourceKey(matchId, analystId));
}

export function hasLocalEdits(matchId, analystId) {
  return editedSessions.has(_sourceKey(matchId, analystId));
}

// ─── Stray text ───────────────────────────────────────────────────────────────

// Events never contain whitespace; a full event has 12 dash-separated fields.
const EVENT_MIN_DASHES = 6;

/**
 * Removes words that were dropped or pasted into the string by accident
 * (e.g. the "Edit Match String" dialog title). Only tokens containing
 * whitespace are touched: they are split, and parts that don't look like
 * events are removed. Recovered events become their own tokens.
 *
 * @returns {{string: string, removed: string[]}}
 */
export function stripStrayWords(rawString) {
  if (!rawString || !/\s/.test(rawString)) return { string: rawString, removed: [] };
  const removed = [];
  const tokens = rawString.split(",").flatMap((token) => {
    const parts = token.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return [token];
    return parts.filter((part) => {
      const isEvent = (part.match(/-/g) || []).length >= EVENT_MIN_DASHES;
      if (!isEvent) removed.push(part);
      return isEvent;
    });
  });
  return { string: tokens.join(","), removed };
}
